// Pure helpers + types for meeting form state. An
// `isRecurring` toggle so the form swaps the single Date field for the
// recurrence panel. The rule itself stays in state when the user
// toggles off (so flipping back doesn't lose typed values), but the
// serialiser drops `recurrenceRule` from the payload whenever
// `isRecurring=false` — the wire shape is the source of truth, the
// form's stash is purely UX.

import type {
  CreateMeetingInput,
  EvidenceLink,
  Meeting,
  MeetingStatus,
  RecurrenceRule,
} from "../../../../types";

export interface MeetingFormState {
  name: string;
  meetingType: string;
  date: string;
  description: string;
  contractReference: string;
  attendees: string[];
  isRecurring: boolean;
  recurrenceRule: RecurrenceRule | undefined;
  evidenceLinks: EvidenceLink[];
  notes: string;
  status: MeetingStatus;
  heldDate: string;
}

export type MeetingFieldErrors = Partial<
  Record<
    | keyof MeetingFormState
    | "form"
    | "recurrenceStartDate"
    | "recurrenceEndDate",
    string
  >
>;

export function buildEmptyMeetingFormState(): MeetingFormState {
  return {
    name: "",
    meetingType: "",
    date: "",
    description: "",
    contractReference: "",
    attendees: [],
    isRecurring: false,
    recurrenceRule: undefined,
    evidenceLinks: [],
    notes: "",
    status: "scheduled",
    heldDate: "",
  };
}

export function stateFromMeeting(item: Meeting): MeetingFormState {
  return {
    name: item.name,
    meetingType: item.meetingType,
    date: item.date,
    description: item.description ?? "",
    contractReference: item.contractReference ?? "",
    attendees: item.attendees ? [...item.attendees] : [],
    isRecurring: !!item.recurrenceRule,
    recurrenceRule: item.recurrenceRule
      ? { ...item.recurrenceRule }
      : undefined,
    evidenceLinks: item.evidenceLinks ? [...item.evidenceLinks] : [],
    notes: item.notes ?? "",
    status: item.status,
    heldDate: item.heldDate ?? "",
  };
}

/**
 * Build the backend Create payload. Strips empty optionals and trims
 * attendee tokens. `recurrenceRule` is only emitted when the user has
 * the Recurring toggle on AND a rule is populated; the form may still
 * carry a stale rule when isRecurring=false (for round-trip stash) and
 * the serialiser is the source of truth for what the backend sees.
 *
 * `ctx.contractEndDate` is used as a fallback end date when the user
 * leaves the recurrence end date blank: the serialiser fills it in so
 * the generated occurrences stop at the contract boundary by default.
 * A user-entered end date always takes precedence.
 */
export function toCreateMeetingInput(
  state: MeetingFormState,
  contractId: string,
  ctx?: { contractEndDate?: string },
): CreateMeetingInput {
  const cleanAttendees = state.attendees
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  // For recurring meetings the date field is the first-occurrence date,
  // sourced from recurrenceRule.startDate. state.date defaults to "" for
  // recurring forms, which fails the backend YYYY-MM-DD regex.
  const date =
    state.isRecurring && state.recurrenceRule?.startDate
      ? state.recurrenceRule.startDate
      : state.date;

  const base: CreateMeetingInput = {
    contractId,
    name: state.name.trim(),
    meetingType: state.meetingType,
    date,
  };
  if (state.description.trim()) base.description = state.description.trim();
  if (state.contractReference.trim()) base.contractReference = state.contractReference.trim();
  if (cleanAttendees.length > 0) base.attendees = cleanAttendees;
  if (state.isRecurring && state.recurrenceRule) {
    const rule = state.recurrenceRule;
    const endDate = rule.endDate ?? ctx?.contractEndDate;
    base.recurrenceRule = endDate ? { ...rule, endDate } : { ...rule };
  }
  if (state.evidenceLinks.length > 0) base.evidenceLinks = state.evidenceLinks;
  if (state.notes.trim()) base.notes = state.notes.trim();
  if (state.status && state.status !== "scheduled") base.status = state.status;
  if (state.status === "held" && state.heldDate) {
    base.heldDate = state.heldDate;
  }
  return base;
}
